import React, { Component, MouseEvent, TouchEvent } from "react";
import {
    addMilliseconds,
    addSeconds,
    getDay,
    getHours,
    getMinutes,
    getSeconds,
    isFuture,
} from "date-fns";
import { action, observable } from "mobx";
import { observer } from "mobx-react";

import { combineFilters } from "@formant/core/common/combineFilters";
import { debounce } from "@formant/core/common/debounce";
import { defined } from "@formant/core/common/defined";
import { LruCache } from "@formant/core/common/LruCache";
import { throttle } from "@formant/core/common/throttle";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { duration } from "@formant/core/model/duration";
import { IAggregateEvent } from "@formant/core/model/IAggregateEvent";
import { IBaseEvent } from "@formant/core/model/IBaseEvent";
import { IDevice } from "@formant/core/model/IDevice";
import { IEvent } from "@formant/core/model/IEvent";
import { IEventOrAggregate } from "@formant/core/model/IEventOrAggregate";
import { IFilter } from "@formant/core/model/IFilter";
import {
    addInterval,
    Interval,
    truncateLocalDate,
    truncateUtcDate,
} from "@formant/core/model/Interval";
import { IOnDemandPresenceTimeRange } from "@formant/core/model/IOnDemandPresenceTimeRange";
import { IScopeFilter } from "@formant/core/model/IScopeFilter";
import { IShare } from "@formant/core/model/IShare";
import { PresenceInterval } from "@formant/core/model/PresenceInterval";
import { Measure } from "@formant/ui/component/common/Measure";
import cssColors from "@formant/ui/style/colorExports.scss";

import { track } from "../../../analytics";
import { IOnDemandRequestEvent } from "../../../store/IOnDemandStore";
import { IPresenceInterval } from "../../../store/IPresenceInterval";
import { SeekOption, seekOptionToEventTypes } from "../../../store/SeekOption";
import {
    appStore,
    eventStore,
    observeStore,
    onDemandStore,
    onlineDevicesStore,
    presenceStore,
} from "../../../stores";
import { captureError } from "../../../utils/captureError";
import { drawCircle } from "../../../utils/drawCircle";
import { formatDateString } from "../../../utils/formatDateString";
import {
    days,
    hours,
    minutes,
    months,
    seconds,
    years,
} from "../../../utils/time";
import { Timer } from "../../common/Timer";
import { EventHover } from "./EventHover";
import * as styles from "./Scrubber.scss";

const minScale = 1000;
const maxScale = duration.month;
const stepPixels = 60;
const hoverRadius = 5;
const size = 5;
const padding = 0.5;
const timeRangeHeight = size * 2 + padding * 3;
const eventCenterY = size * 4 + padding * 2 + size / 2;
const strokeBasis = 2;
const playheadWidth = 2.5;
const eventWidth = 2.5;
const aggregatePreviewEventsMax = 5;

const animateClampMs: number = duration.second / 15;
const timeUpdateMs: number = 0.1 * duration.second;
const rafMs: number = 0.7 * duration.second;
const positionUpdateMs: number = duration.second;

interface IScrubberProps<T extends IBaseEvent> {
    context: IFilter;
    filter?: IFilter;
    share?: IShare;
    onSelectEvent: (event: IEventOrAggregate) => void;
    device?: IDevice;
    selectedEvent?: T;
    onChangeSelectedEvent?: (event: T) => void;
    seekOption: SeekOption;
}

@observer
export class Scrubber<T extends IBaseEvent> extends Component<
    IScrubberProps<T>
> {
    @observable
    private position: Date = new Date();
    @observable
    private width: number = 0;
    @observable
    private height: number = 0;
    @observable
    private hoverRange: "start" | "end" | number | undefined;
    private canvas?: HTMLCanvasElement;
    private ctx?: CanvasRenderingContext2D;
    private time?: Date;
    private center: Date = new Date();
    private mounted: boolean = false;
    private draggingTimeMarker: boolean = false;
    private draggingRange: "start" | "end" | number | undefined;
    private moving: boolean = false;
    private lastFrame: number = 0;
    private contextPresence: IPresenceInterval[] = [];
    private filterPresence: IPresenceInterval[] = [];
    private eventsOrAggregates: IEventOrAggregate[] = [];
    private ticks: [Date, string][] = [];
    private labels = new LruCache<string, HTMLCanvasElement>({
        name: "Scrubber-labels-cache",
        max: 1000,
        fastStringify: true,
    });
    private onDemandRequestEvents: IOnDemandRequestEvent[] = [];
    private onDemandPatternImg: HTMLImageElement | undefined = undefined;
    private onDemandPattern: CanvasPattern | undefined = undefined;

    private stopMoving = debounce(() => (this.moving = false), 2000);

    private colors: { [key: string]: string } = {};

    private throttledAnimate = throttle(() => this.raf(), animateClampMs);

    public componentDidMount() {
        const { canvas } = this;

        this.colors = {
            moduleColor: getComputedStyle(document.body).getPropertyValue(
                "--formant-color-module-content",
            ),
            onDemandColor: cssColors.onDemandColor,
            fieldColor: getComputedStyle(document.body).getPropertyValue(
                "--formant-color-primary-2",
            ),
            backgroundColor: cssColors.backgroundColor,
            primaryColor: cssColors.primaryColor,
            foregroundColor: getComputedStyle(document.body).getPropertyValue(
                "--formant-color-foreground-2",
            ),
            infoColor: cssColors.infoColor,
            warningColor: cssColors.warningColor,
            errorColor: cssColors.errorColor,
            criticalColor: cssColors.criticalColor,
            interventionColor: cssColors.interventionColor,
            teleopColor: cssColors.teleopColor,
            portForwardingColor: cssColors.portForwardingColor,
            commandRequestColor: cssColors.commandRequestColor,
            commandDeliveryColor: cssColors.commandDeliveryColor,
            commandResponseColor: cssColors.commandResponseColor,
            customColor: cssColors.customColor,
            commentColor: cssColors.commentColor,
            annotationColor: cssColors.annotationColor,
            taskSummaryColor: cssColors.taskSummaryColor,
            systemColor: cssColors.systemColor,
        };

        if (!canvas) {
            return;
        }

        canvas.addEventListener("wheel", this.onWheel);

        this.ctx = canvas.getContext("2d") || undefined;

        this.mounted = true;

        this.lastFrame = window.performance.now();
        this.raf();
        this.onDemandPatternImg = new Image();
        this.onDemandPatternImg.src =
            "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAAL0lEQVQYV2MM85ryn4GBgeFUWDaIYmAECcA4ZqumMjAqLPgPVgHigFWABGAckEoAgRsS5wYNL4UAAAAASUVORK5CYII=";
    }

    public componentWillUnmount() {
        if (this.canvas) {
            this.canvas.removeEventListener("wheel", this.onWheel);
        }
        this.mounted = false;
    }

    public render() {
        const { hoverEvent, hoverAggregateEvent } = observeStore;
        const hoverTime =
            (hoverEvent?.time && parseIsoDate(hoverEvent.time)) ||
            (hoverAggregateEvent &&
                new Date(
                    (defined(hoverAggregateEvent?.start.getTime()) +
                        defined(hoverAggregateEvent?.end.getTime())) /
                        2,
                ));
        const hoverPosition = hoverTime && this.timeToCoordinate(hoverTime);

        this.update();

        const aggregatePreviewEvents =
            (hoverAggregateEvent &&
                eventStore.query({
                    ...hoverAggregateEvent.filter,
                    start: hoverAggregateEvent.start.toISOString(),
                    end: addMilliseconds(
                        hoverAggregateEvent.end,
                        1,
                    ).toISOString(),
                    count: aggregatePreviewEventsMax,
                })) ||
            undefined; // TODO loading state

        return (
            <Measure
                onMeasureResize={this.onResize}
                className={styles.scrubber}
            >
                <Timer
                    interval={positionUpdateMs}
                    onTick={() => {
                        this.position = this.center;
                    }}
                    quantizeStart={duration.second}
                    delay={duration.second / 2}
                />
                <Timer
                    interval={timeUpdateMs}
                    onTick={this.onTick}
                    quantizeStart={duration.second}
                />
                <Timer
                    interval={rafMs}
                    onTick={this.animateLoop}
                    quantizeStart={duration.second}
                />

                <EventHover
                    event={hoverEvent}
                    aggregateEvent={hoverAggregateEvent}
                    aggregatePreviewEvents={aggregatePreviewEvents}
                    position={hoverPosition}
                />
                <canvas
                    ref={_ => (this.canvas = _ || undefined)}
                    onMouseDown={this.onMouseDown}
                    onMouseMove={this.onMouseMove}
                    onMouseUp={this.onMouseMove}
                    onMouseOut={this.onMouseOut}
                    onTouchStart={this.onTouchStart}
                    onTouchEnd={this.onTouchEnd}
                    onTouchMove={this.onTouchMove}
                />
            </Measure>
        );
    }

    private get scale() {
        return (appStore.timelineScale * 0.5) / stepPixels;
    }

    private get span() {
        return this.scale * this.width;
    }

    private get step() {
        return this.scale * stepPixels;
    }

    private get interval(): Interval {
        const { step } = this;
        if (step > duration.day) {
            return "month";
        } else if (step > duration.hour) {
            return "day";
        } else if (step > duration.minute) {
            return "hour";
        } else {
            return "minute";
        }
    }

    private get presenceInterval(): PresenceInterval {
        const { step } = this;

        if (step <= duration.day) {
            return "minute";
        } else if (step <= duration.month) {
            return "hour";
        } else {
            return "day";
        }
    }

    private get presenceQueryStartTime(): Date {
        const { clampToRetentionPeriod } = observeStore;
        const start = new Date(this.position.getTime() - (this.span / 2) * 1.1);
        const interval = this.presenceInterval;
        return clampToRetentionPeriod(truncateUtcDate(start, interval));
    }

    private get presenceQueryEndTime(): Date {
        const end = new Date(this.position.getTime() + (this.span / 2) * 1.1);
        const interval = this.presenceInterval;
        return addInterval(truncateUtcDate(end, interval), interval);
    }

    private get start(): Date {
        return new Date(this.center.getTime() - this.span / 2);
    }

    private get end(): Date {
        return new Date(this.center.getTime() + this.span / 2);
    }

    private update() {
        this.updatePresence();
        this.updateEvents();
        this.updateTicks();
    }

    private updateTicks() {
        this.ticks = this.calculateTicks();
    }

    private calculateTicks(): [Date, string][] {
        const { start, end, step } = this;

        const formatYear = (_: Date) => formatDateString(_, "yyyy");
        const formatMonth = (_: Date) =>
            formatDateString(_, "MMM").toUpperCase();
        const formatDay = (_: Date) =>
            formatDateString(_, "MMM d").toUpperCase();
        const formatHour = (_: Date) =>
            formatDateString(_, "h a").toLowerCase();
        const formatMinute = (_: Date) =>
            formatDateString(_, "h:mm a").toLowerCase();
        const formatSecond = (_: Date) =>
            formatDateString(_, "h:mm:ss a").toLowerCase();

        if (step <= 10 * duration.second) {
            return [...seconds(start, end)]
                .filter(_ => getSeconds(_) % 10 === 0)
                .map(_ => [_, formatSecond(_)]);
        } else if (step <= duration.minute) {
            return [...minutes(start, end)].map(_ => [_, formatMinute(_)]);
        } else if (step <= 10 * duration.minute) {
            return [...minutes(start, end)]
                .filter(_ => getMinutes(_) % 10 === 0)
                .map(_ => [_, formatMinute(_)]);
        } else if (step <= duration.hour) {
            return [...hours(start, end)].map(_ => [_, formatHour(_)]);
        } else if (step <= 6 * duration.hour) {
            return [...hours(start, end)]
                .filter(_ => getHours(_) % 6 === 0)
                .map(_ => [_, formatHour(_)]);
        } else if (step <= duration.day) {
            return [...days(start, end)].map(_ => [_, formatDay(_)]);
        } else if (step <= 7 * duration.day) {
            return [...days(start, end)]
                .filter(_ => getDay(_) === 0)
                .map(_ => [_, formatDay(_)]);
        } else if (step <= duration.month) {
            return [...months(start, end)].map(_ => [_, formatMonth(_)]);
        } else {
            return [...years(start, end)].map(_ => [_, formatYear(_)]);
        }
    }

    private updateEvents() {
        const { context, share, seekOption, device } = this.props;
        const { interval } = this;

        if (share) {
            return;
        }

        const eventTypes =
            (seekOption !== "Data presence" &&
                seekOptionToEventTypes(seekOption)) ||
            undefined;

        const filter = {
            ...combineFilters(context, this.props.filter),
            eventTypes,
        };

        const start = truncateLocalDate(
            new Date(this.position.getTime() - (this.span / 2) * 1.1),
            interval,
        );
        const end = addInterval(
            truncateLocalDate(
                new Date(this.position.getTime() + (this.span / 2) * 1.1),
                interval,
            ),
            interval,
        );

        const millisecondsPerEvent = this.scale * eventWidth;

        this.eventsOrAggregates =
            (start &&
                end &&
                eventStore.queryEventOrAggregates(
                    filter,
                    interval,
                    start,
                    end,
                    millisecondsPerEvent,
                )) ||
            this.eventsOrAggregates;

        const deviceId = device?.id;
        this.onDemandRequestEvents =
            (deviceId &&
                onDemandStore
                    .getRecentOnDemandRequests(
                        deviceId,
                        this.start.toISOString(),
                        this.end.toISOString(),
                    )
                    .filter(x => !x.finished)) ||
            this.onDemandRequestEvents;
    }

    private updatePresence() {
        const { context } = this.props;

        const combinedFilter = combineFilters(context, this.props.filter);

        const start = this.presenceQueryStartTime.toISOString();
        const end = this.presenceQueryEndTime.toISOString();
        const interval = this.presenceInterval;

        const contextPresence = presenceStore.query({
            ...context,
            start,
            end,
            interval,
        });

        const filterPresence = presenceStore.query({
            ...combinedFilter,
            // ensure combinedFilter is similar shape to above query
            tags: combinedFilter.tags || {},
            start,
            end,
            interval,
        });

        this.contextPresence = contextPresence || this.contextPresence;
        this.filterPresence = filterPresence || this.filterPresence;
    }

    private draw() {
        const { ctx, ticks, width, height, start, end, timeToCoordinate } =
            this;
        const { device, filter, selectedEvent } = this.props;
        const live = observeStore.mode === "live";
        const time = this.time || observeStore.time;
        const { hoverEvent, hoverAggregateEvent, selectedAggregateEvent } =
            observeStore;

        if (!ctx) {
            return;
        }

        const now = Date.now();

        ctx.clearRect(0, 0, width, height);

        const drawBackground = () => {
            ctx.save();
            ctx.fillStyle = this.colors.moduleColor;
            ctx.beginPath();
            // highlight region up until now
            rect(
                ctx,
                0,
                size * 3,
                Math.min(
                    timeToCoordinate(new Date(now)) - timeToCoordinate(start),
                    width,
                ),
                size * 2 + padding * 3,
            );
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        };

        const drawOnDemandPresence = () => {
            if (
                device &&
                device.id &&
                device.state &&
                device.state.commandProgress &&
                device.state.onDemand
            ) {
                const onDemandState = device.state.onDemand;
                const online = onlineDevicesStore.isOnline(device.id);
                const filterSet: Set<string> = new Set();
                if (filter && filter.names) {
                    filter.names.forEach(x => {
                        filterSet.add(x);
                    });
                }
                ctx.save();

                ctx.fillStyle = this.colors.onDemandColor;

                const drawOnDemandPresenceBar = (
                    onDemandPresenceStartTime: number,
                    onDemandPresenceEndTime: number,
                ) => {
                    ctx.beginPath();
                    const startTimeCoord = timeToCoordinate(
                        new Date(onDemandPresenceStartTime),
                    );
                    const endTimeCoord = timeToCoordinate(
                        new Date(onDemandPresenceEndTime),
                    );
                    rect(
                        ctx,
                        startTimeCoord,
                        size * 2 - padding,
                        endTimeCoord - startTimeCoord,
                        size,
                        size / 2,
                    );

                    ctx.closePath();
                    ctx.fill();
                };

                const minPresenceTime = start.getTime();
                const maxPresenceTime = end.getTime();

                let viewablePresenceRanges: IOnDemandPresenceTimeRange[] = [];
                onDemandState.buffers.forEach(buffer => {
                    buffer.streams.forEach(stream => {
                        // if we are filtering by on-demand and this stream doesn't match the filter, continue
                        if (
                            filterSet.size > 0 &&
                            !filterSet.has(stream.streamName)
                        ) {
                            return;
                        }
                        stream.presence.forEach(presence => {
                            presence.timeRanges.forEach(range => {
                                const rangeStart = range.start;
                                const rangeEnd = range.end;
                                // never show range in past now
                                if (rangeEnd > now) {
                                    range.end = now;
                                }

                                // if a range is close to now and device is online, just show data to confuse users less
                                // this is because there's a delay in how fast agent sends up presence ( every 1 minute )
                                if (
                                    online &&
                                    rangeEnd > now - 2 * duration.minute
                                ) {
                                    range.end = now;
                                }

                                // is the range of presence in our timeline view
                                if (
                                    // does a time range start in our view
                                    (minPresenceTime <= rangeStart &&
                                        rangeStart <= maxPresenceTime) ||
                                    // does time range end in our view
                                    (minPresenceTime <= rangeEnd &&
                                        rangeEnd <= maxPresenceTime) ||
                                    // does the range surround our view
                                    (rangeStart <= minPresenceTime &&
                                        maxPresenceTime <= rangeEnd)
                                ) {
                                    viewablePresenceRanges.push(range);
                                }
                            });
                        });
                    });
                });

                // TODO: figure out some way to downsample, for now just show last 1000
                if (viewablePresenceRanges.length > 1000) {
                    viewablePresenceRanges = viewablePresenceRanges.sort(
                        (a, b) => b.end - a.end,
                    );
                    viewablePresenceRanges = viewablePresenceRanges.slice(
                        0,
                        1000,
                    );
                }

                viewablePresenceRanges.forEach(range => {
                    drawOnDemandPresenceBar(range.start, range.end);
                });

                ctx.restore();
            }
        };

        const drawOnDemandRequests = () => {
            if (device && device.id) {
                if (!this.onDemandPattern && this.onDemandPatternImg) {
                    const pattern = ctx.createPattern(
                        this.onDemandPatternImg,
                        "repeat",
                    );
                    if (pattern) {
                        this.onDemandPattern = pattern;
                    }
                }
                ctx.save();

                const drawOnDemandRequest = (
                    onDemandPresenceStartTime: number,
                    onDemandPresenceEndTime: number,
                    onDemandPresenceCurrentime: number,
                ) => {
                    const startTimeCoord = timeToCoordinate(
                        new Date(onDemandPresenceStartTime),
                    );
                    const endTimeCoord = timeToCoordinate(
                        new Date(onDemandPresenceEndTime),
                    );
                    const currentTimeCoord = timeToCoordinate(
                        new Date(onDemandPresenceCurrentime),
                    );

                    ctx.beginPath();
                    if (this.onDemandPattern) {
                        ctx.fillStyle = this.onDemandPattern;
                    } else {
                        ctx.fillStyle = this.colors.onDemandColor;
                    }
                    rect(
                        ctx,
                        startTimeCoord,
                        size * 2 - padding,
                        endTimeCoord - startTimeCoord,
                        size,
                        size / 2,
                    );
                    ctx.closePath();
                    ctx.fill();

                    ctx.beginPath();
                    ctx.fillStyle = this.colors.infoColor;
                    rect(
                        ctx,
                        startTimeCoord,
                        size * 2 - padding,
                        currentTimeCoord - startTimeCoord,
                        size,
                        size / 2,
                    );
                    ctx.closePath();
                    ctx.fill();

                    // draw white highight ring
                    ctx.globalAlpha =
                        0.5 + Math.sin(new Date().getTime() / 500) * 0.5;
                    ctx.strokeStyle = "white";
                    ctx.beginPath();
                    rect(
                        ctx,
                        startTimeCoord,
                        size * 2 - padding,
                        endTimeCoord - startTimeCoord,
                        size,
                        size / 2,
                    );
                    ctx.closePath();
                    ctx.stroke();

                    ctx.globalAlpha = 1;
                };

                const minPresenceTime = start.getTime();
                const maxPresenceTime = end.getTime();

                const viewablePresenceRanges = this.onDemandRequestEvents
                    .map(command => {
                        const rangeStart = command.start.getTime();
                        let rangeEnd = command.end.getTime();
                        if (rangeEnd > now) {
                            rangeEnd = now;
                        }
                        const rangeCurrent =
                            rangeStart +
                            (command.progress / 100) * (rangeEnd - rangeStart);
                        return { rangeStart, rangeEnd, rangeCurrent };
                    })
                    .filter(command => {
                        const { rangeStart, rangeEnd } = command;
                        // is the range of presence in our timeline view
                        if (
                            // does a time range start in our view
                            (minPresenceTime <= rangeStart &&
                                rangeStart <= maxPresenceTime) ||
                            // does time range end in our view
                            (minPresenceTime <= rangeEnd &&
                                rangeEnd <= maxPresenceTime) ||
                            // does the range surround our view
                            (rangeStart <= minPresenceTime &&
                                maxPresenceTime <= rangeEnd)
                        ) {
                            return true;
                        }
                        return false;
                    });

                viewablePresenceRanges.forEach(range => {
                    if (range) {
                        drawOnDemandRequest(
                            range.rangeStart,
                            range.rangeEnd,
                            range.rangeCurrent,
                        );
                    }
                });

                ctx.restore();
            }
        };

        const drawPresence = (presence: IPresenceInterval[], color: string) => {
            ctx.save();

            ctx.fillStyle = color;

            ctx.beginPath();

            presence.forEach(breadcrumb => {
                const breadcrumbStart = new Date(
                    Math.max(start.getTime(), breadcrumb.start.getTime()),
                );
                const breadcrumbEnd = new Date(
                    Math.min(end.getTime(), breadcrumb.end.getTime(), now),
                );

                const startX = timeToCoordinate(
                    this.clampTime(breadcrumbStart),
                );
                const endX = timeToCoordinate(this.clampTime(breadcrumbEnd));

                if (endX > startX) {
                    rect(
                        ctx,
                        startX,
                        size * 3 + padding,
                        endX - startX,
                        size,
                        size / 2,
                    );
                }
            });

            ctx.closePath();

            ctx.fill();

            ctx.restore();
        };

        const drawContextBreadcrumbs = () =>
            drawPresence(this.contextPresence, this.colors.fieldColor);

        const drawFilterBreadcrumbs = () =>
            drawPresence(this.filterPresence, this.colors.infoColor);

        const drawTimeGridLines = () => {
            ctx.save();

            ctx.lineWidth = 1;
            ctx.strokeStyle = this.colors.backgroundColor;
            ctx.beginPath();

            ticks
                .filter(([_]) => _.getTime() < now)
                .forEach(([_]) => {
                    const x = timeToCoordinate(_);
                    ctx.moveTo(x, size * 3);
                    ctx.lineTo(x, size * 5 + padding * 3);
                });

            ctx.closePath();

            ctx.stroke();

            ctx.restore();
        };

        const drawTimeLabels = () => {
            ctx.save();
            const y = size * 6 + padding * 4;
            const pixelRatio = window.devicePixelRatio;
            this.ticks.forEach(([tick, label]) => {
                const x = timeToCoordinate(tick);
                const cached = this.getLabel(label);
                if (cached) {
                    ctx.drawImage(
                        cached,
                        x - cached.width / 2 / pixelRatio,
                        y,
                        cached.width / pixelRatio,
                        cached.height / pixelRatio,
                    );
                }
            });
            ctx.restore();
        };

        const {
            infoColor,
            warningColor,
            errorColor,
            criticalColor,
            interventionColor,
            teleopColor,
            portForwardingColor,
            commandRequestColor,
            commandDeliveryColor,
            commandResponseColor,
            customColor,
            commentColor,
            annotationColor,
            taskSummaryColor,
            systemColor,
        } = this.colors;

        const renderEventTick = (x: number, y: number) => {
            const w = eventWidth;
            const h = size;
            x -= w / 2;
            y -= h / 2;
            rect(ctx, x, y, w, h, size / 4);
        };

        const renderCommentDiamond = (x: number, y: number) => {
            const w = 4.5;
            const h = size;
            x -= w / 2;
            y -= h / 2;
            ctx.moveTo(x + w / 2, y);
            ctx.lineTo(x + w, y + h / 2);
            ctx.lineTo(x + w / 2, y + h);
            ctx.lineTo(x, y + h / 2);
        };

        interface IEventTypeRenderer {
            matches: (event: IEvent) => boolean;
            color: string;
            render: (x: number, y: number) => void;
        }

        const eventRenderers: IEventTypeRenderer[] = [
            {
                matches: _ =>
                    (_.type === "datapoint-event" ||
                        _.type === "triggered-event" ||
                        _.type === "device-online" ||
                        _.type === "device-offline" ||
                        _.type === "stateful") &&
                    _.severity === "critical",
                color: criticalColor,
                render: renderEventTick,
            },
            {
                matches: _ =>
                    (_.type === "datapoint-event" ||
                        _.type === "triggered-event" ||
                        _.type === "device-online" ||
                        _.type === "device-offline" ||
                        _.type === "stateful") &&
                    _.severity === "error",
                color: errorColor,
                render: renderEventTick,
            },
            {
                matches: _ =>
                    (_.type === "datapoint-event" ||
                        _.type === "triggered-event" ||
                        _.type === "device-online" ||
                        _.type === "device-offline" ||
                        _.type === "stateful") &&
                    _.severity === "warning",
                color: warningColor,
                render: renderEventTick,
            },
            {
                matches: _ =>
                    (_.type === "datapoint-event" ||
                        _.type === "triggered-event" ||
                        _.type === "device-online" ||
                        _.type === "device-offline" ||
                        _.type === "stateful") &&
                    _.severity === "info",
                color: infoColor,
                render: renderEventTick,
            },
            {
                matches: _ => _.type === "intervention-request",
                color: interventionColor,
                render: renderEventTick,
            },
            {
                matches: _ => _.type === "command-request",
                color: commandRequestColor,
                render: renderEventTick,
            },
            {
                matches: _ => _.type === "command-delivery",
                color: commandDeliveryColor,
                render: renderEventTick,
            },
            {
                matches: _ => _.type === "command-response",
                color: commandResponseColor,
                render: renderEventTick,
            },
            {
                matches: _ => _.type === "teleop-session-record",
                color: teleopColor,
                render: renderEventTick,
            },
            {
                matches: _ => _.type === "port-forwarding-session-record",
                color: portForwardingColor,
                render: renderEventTick,
            },
            {
                matches: _ => _.type === "custom",
                color: customColor,
                render: renderEventTick,
            },
            {
                matches: _ => _.type === "system",
                color: systemColor,
                render: renderEventTick,
            },
            {
                matches: _ => _.type === "comment",
                color: commentColor,
                render: renderCommentDiamond,
            },
            {
                matches: _ => _.type === "annotation",
                color: annotationColor,
                render: renderEventTick,
            },
            {
                matches: _ => _.type === "task-summary",
                color: taskSummaryColor,
                render: renderEventTick,
            },
        ];

        const drawSelectedEventTimeRange = () => {
            if (!selectedEvent) {
                return;
            }
            const { time: selectedEventTime, endTime: selectedEventEndTime } =
                selectedEvent;
            if (!selectedEventEndTime) {
                return;
            }
            const timeRangeStart = parseIsoDate(selectedEventTime);
            const timeRangeEnd = parseIsoDate(selectedEventEndTime);
            if (timeRangeEnd.getTime() < timeRangeStart.getTime()) {
                return;
            }
            const x1 = timeToCoordinate(timeRangeStart);
            const x2 = timeToCoordinate(timeRangeEnd);
            const startHover =
                this.hoverRange === "start" || this.draggingRange === "start";
            const endHover =
                this.hoverRange === "end" || this.draggingRange === "end";
            const renderer = eventRenderers.find(_ =>
                _.matches(selectedEvent as IEvent),
            );
            if (renderer) {
                drawTimeRange(x1, x2, renderer.color, startHover, endHover);
            }
        };

        const drawTimeRange = (
            x1: number,
            x2: number,
            style: string,
            startHover: boolean = false,
            endHover: boolean = false,
        ) => {
            ctx.save();

            // center line
            ctx.lineWidth = strokeBasis / 2;
            ctx.strokeStyle = style;
            ctx.beginPath();
            ctx.moveTo(x1, size * 3 + timeRangeHeight / 2);
            ctx.lineTo(x2, size * 3 + timeRangeHeight / 2);
            ctx.closePath();
            ctx.stroke();

            // container
            ctx.lineWidth =
                this.hoverRange || this.draggingRange
                    ? strokeBasis * 2
                    : strokeBasis;
            ctx.strokeStyle = style;
            ctx.fillStyle = style;
            ctx.beginPath();
            rect(
                ctx,
                x1 - strokeBasis * 3,
                size * 3,
                x2 - x1 + strokeBasis * 6,
                timeRangeHeight,
                size / 2,
            );
            ctx.closePath();
            ctx.stroke();

            ctx.globalAlpha = 0.3;
            ctx.fill();
            ctx.globalAlpha = 1;

            // start endpoint
            ctx.beginPath();
            drawCircle(ctx, x1, size * 3 + timeRangeHeight / 2, strokeBasis);
            ctx.closePath();
            ctx.fillStyle = style;
            ctx.fill();
            if (startHover) {
                ctx.lineWidth = strokeBasis / 2;
                ctx.strokeStyle = "white";
                ctx.stroke();
            }

            // end endpoint
            ctx.beginPath();
            drawCircle(ctx, x2, size * 3 + timeRangeHeight / 2, strokeBasis);
            ctx.closePath();
            ctx.fillStyle = style;
            ctx.fill();
            if (endHover) {
                ctx.lineWidth = strokeBasis / 2;
                ctx.strokeStyle = "white";
                ctx.stroke();
            }

            ctx.restore();
        };

        const drawPlayhead = () => {
            ctx.save();
            ctx.fillStyle = live ? this.colors.primaryColor : "white";
            const x = timeToCoordinate(time);
            ctx.beginPath();
            rect(
                ctx,
                x - playheadWidth / 2,
                size,
                playheadWidth,
                size * 4 + padding * 3,
            );
            drawCircle(ctx, x, size * 1.5, size * 1.5);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        };

        const drawEvents = () => {
            ctx.save();

            // Draw raw unaggregated events
            eventRenderers.forEach(({ matches, color, render }) => {
                const matching = this.eventsOrAggregates.filter(
                    ({ event }) =>
                        event &&
                        matches(event) &&
                        this.isWithinScope(parseIsoDate(event.time)),
                );
                if (matching.length === 0) {
                    return;
                }

                ctx.fillStyle = color;
                ctx.beginPath();

                matching.forEach(({ event }) => {
                    // skip the hovered event
                    if (hoverEvent?.id === event?.id) {
                        return;
                    }
                    const x = timeToCoordinate(
                        parseIsoDate(defined(event).time),
                    );
                    render(x, eventCenterY);
                });

                ctx.closePath();
                ctx.fill();
            });

            // Draw aggregated events
            this.eventsOrAggregates.forEach(({ aggregate }) => {
                if (!aggregate) {
                    return;
                }
                const x1 = timeToCoordinate(aggregate.start);
                // DEV-3907. If the start and end times are the same,
                // use our smallest duration offset for the tick size
                const x2 = timeToCoordinate(
                    aggregate.end.getTime() === aggregate.start.getTime()
                        ? addSeconds(aggregate.end, 1)
                        : aggregate.end,
                );

                if (
                    !this.isWithinScope(aggregate.start) ||
                    !this.isWithinScope(aggregate.end)
                ) {
                    return;
                }

                const deltaX = x2 - x1;
                const bold =
                    // bold if this aggregate is hovered
                    (hoverAggregateEvent?.start.getTime() ===
                        aggregate.start.getTime() &&
                        hoverAggregateEvent.end.getTime() ===
                            aggregate.end.getTime()) ||
                    // or if it is selected
                    (selectedAggregateEvent?.start.getTime() ===
                        aggregate.start.getTime() &&
                        selectedAggregateEvent.end.getTime() ===
                            aggregate.end.getTime());
                ctx.lineWidth = strokeBasis / (bold ? 1 : 2);
                ctx.strokeStyle = bold ? this.colors.primaryColor : "white";
                ctx.fillStyle = bold ? this.colors.primaryColor : "white";

                // label at center of horizontal line span
                const center = x1 + deltaX / 2;
                const label = this.getLabel(
                    (aggregate as IAggregateEvent).count.toLocaleString(),
                    "black",
                );
                const pixelRatio = window.devicePixelRatio;
                // draw label if it fits between the vertical strokes with padding
                if (
                    label &&
                    (2 * label.width) / pixelRatio - padding < deltaX
                ) {
                    // the bracket the embraces the time span
                    ctx.beginPath();
                    fancyBracket(
                        ctx,
                        x1,
                        eventCenterY - size / 2,
                        x2 - x1,
                        size * 2,
                    );
                    ctx.stroke();
                    // background of the label
                    ctx.beginPath();
                    tab(
                        ctx,
                        center - label.width / 2 / pixelRatio - padding,
                        eventCenterY - size / 2,
                        label.width / pixelRatio + 2 * padding,
                        label.height / pixelRatio + 2 * padding,
                        size / 2,
                    );
                    ctx.closePath();
                    ctx.fill();
                    ctx.stroke();
                    // label
                    ctx.drawImage(
                        label,
                        center - label.width / 2 / pixelRatio,
                        eventCenterY - size / 2,
                        label.width / pixelRatio,
                        label.height / pixelRatio,
                    );
                }
                // Too small for a label
                else {
                    ctx.beginPath();
                    tab(
                        ctx,
                        x1,
                        eventCenterY - size / 2,
                        x2 - x1,
                        size * 2,
                        size / 2,
                    );
                    ctx.closePath();
                    ctx.fill();
                    ctx.stroke();
                }
            });

            // render the hovered event last
            if (hoverEvent) {
                const renderer = eventRenderers.find(_ =>
                    _.matches(hoverEvent),
                );
                if (renderer) {
                    const x1 = timeToCoordinate(parseIsoDate(hoverEvent.time));
                    ctx.fillStyle = renderer.color;
                    ctx.beginPath();
                    renderer.render(x1, eventCenterY);
                    ctx.closePath();
                    ctx.fill();
                    ctx.strokeStyle = "white";
                    ctx.lineWidth = strokeBasis / 2;
                    ctx.stroke();
                    // time range line segment
                    if (hoverEvent.endTime) {
                        const x2 = timeToCoordinate(
                            parseIsoDate(hoverEvent.endTime),
                        );
                        if (x2 > x1) {
                            drawTimeRange(x1, x2, renderer.color);
                        }
                    }
                }
            }

            ctx.restore();
        };

        drawBackground();
        drawTimeLabels();
        drawContextBreadcrumbs();
        drawFilterBreadcrumbs();
        drawOnDemandPresence();
        drawOnDemandRequests();
        drawTimeGridLines();
        drawPlayhead();
        drawEvents();
        drawSelectedEventTimeRange();
    }

    private raf = () => {
        window.requestAnimationFrame(this.animate);
    };

    private animate = (time: number) => {
        const delta = this.lastFrame ? time - this.lastFrame : 0;
        this.lastFrame = time;

        this.increment(delta);
        this.move(delta);
        this.draw();

        if (this.mounted) {
            if (
                this.moving ||
                this.draggingTimeMarker ||
                appStore.timelineScale < duration.minute
            ) {
                this.throttledAnimate();
            } else if (observeStore.mode !== "pause") {
                this.throttledAnimate();
            }
        }
    };

    private move(delta: number) {
        if (this.moving || this.draggingTimeMarker) {
            return;
        }

        const time = this.time || observeStore.time;

        const offset = time.getTime() - this.center.getTime();
        if (Math.abs(offset) > this.span * 3) {
            this.center = time;
        } else {
            this.center = new Date(
                this.center.getTime() + Math.min(delta, 200) * 0.005 * offset,
            );
        }
    }

    // Called in animation loop to update in real time the locally cached time value
    private increment(delta: number) {
        const { mode, speed } = observeStore;

        let time: Date | undefined;
        if (mode === "live") {
            time = new Date();
        } else if (mode === "play") {
            time = new Date(
                (this.time || observeStore.time).getTime() + delta * speed,
            );
        }

        if (time) {
            time = this.clampTime(time);
            const scope = this.props.share?.scope;
            const shareEnd = scope?.end && parseIsoDate(scope.end);
            if (shareEnd && time > shareEnd) {
                observeStore.mode = "pause";
            }
            this.time = time;
        }
    }

    private get scope(): IScopeFilter {
        const shareScope = this.props.share?.scope;

        const start = shareScope?.start
            ? parseIsoDate(shareScope.start)
            : undefined;

        const end = shareScope?.end ? parseIsoDate(shareScope.end) : undefined;

        return {
            start: start?.toISOString() || undefined,
            end: end?.toISOString() || undefined,
        };
    }

    private isWithinScope(time: Date): boolean {
        const scope = this.scope;
        const start = scope.start ? parseIsoDate(scope.start) : undefined;
        const end = scope.end ? parseIsoDate(scope.end) : undefined;

        return !((start && time < start) || (end && time > end));
    }

    private clampTime(time: Date): Date {
        const now = new Date();
        const scope = this.scope;
        const start = scope.start ? parseIsoDate(scope.start) : undefined;
        const end = scope.end ? parseIsoDate(scope.end) : undefined;

        if (start && time < start) {
            time = start;
        }
        if (end && time > end) {
            time = end;
        }
        if (isFuture(time)) {
            time = now;
        }
        return time;
    }

    private trackScrubber = throttle(
        () => track("Scrubber"),
        5 * duration.second,
    );

    // Called in user action event handler to update time position
    private onSelectTime = (time: Date) => {
        observeStore.pauseAt(this.clampTime(time));
        this.trackScrubber();
    };

    private animateLoop = () => {
        if (this.mounted) {
            this.raf();
        }
    };

    private onTick = () => {
        if (this.time) {
            if (observeStore.mode !== "pause") {
                observeStore.setTime(this.time);
            }
            this.time = undefined;
        }

        observeStore.updateRange(
            this.presenceQueryStartTime,
            this.presenceQueryEndTime,
        );
    };

    private timeToCoordinate = (time: Date) =>
        ((time.getTime() + this.span / 2 - this.center.getTime()) / this.span) *
        this.width;

    private coordinateToTime = (x: number) =>
        new Date(
            (x / this.width) * this.span +
                this.center.getTime() -
                this.span / 2,
        );

    private mouseToTime = (clientX: number) =>
        this.coordinateToTime(clientX - defined(this.canvas).offsetLeft);

    private mouseToEvent(
        clientX: number,
        clientY: number,
    ): IEventOrAggregate | undefined {
        const { eventsOrAggregates, timeToCoordinate } = this;
        const clientRectTop = defined(this.canvas).getBoundingClientRect().top;
        if (
            clientY - clientRectTop < eventCenterY - size / 2 ||
            clientY - clientRectTop > eventCenterY + 2 * size
        ) {
            return;
        }
        const near = eventsOrAggregates
            .map(eventOrAggregate => {
                const { event, aggregate } = eventOrAggregate;
                if (event) {
                    return {
                        distance: Math.abs(
                            timeToCoordinate(parseIsoDate(event.time)) -
                                clientX,
                        ),
                        eventOrAggregate,
                    };
                } else if (aggregate) {
                    const x1 = timeToCoordinate(aggregate.start);
                    const x2 = timeToCoordinate(aggregate.end);
                    const distance =
                        clientX >= x1 && clientX <= x2
                            ? 0
                            : Math.min(
                                  Math.abs(x1 - clientX),
                                  Math.abs(x2 - clientX),
                              );
                    return { distance, eventOrAggregate };
                } else {
                    throw new Error();
                }
            })
            .filter(({ distance }) => distance < hoverRadius)
            .sort(({ distance: a }, { distance: b }) => a - b);

        return {
            event: near?.[0]?.eventOrAggregate.event,
            aggregate: near?.[0]?.eventOrAggregate.aggregate,
        };
    }

    private mouseToRange(
        clientX: number,
        clientY: number,
    ): "start" | "end" | number | undefined {
        const { timeToCoordinate } = this;
        const { selectedEvent, onChangeSelectedEvent } = this.props;
        if (!selectedEvent || !onChangeSelectedEvent) {
            return;
        }
        const clientRectTop = defined(this.canvas).getBoundingClientRect().top;
        if (Math.abs(eventCenterY - (clientY - clientRectTop)) > size * 2) {
            return;
        }
        const { time, endTime } = selectedEvent;
        if (!endTime) {
            return;
        }
        const start = parseIsoDate(time);
        const end = parseIsoDate(endTime);
        const startX = timeToCoordinate(start);
        const endX = timeToCoordinate(end);
        const startDelta = Math.abs(startX - clientX);
        const endDelta = Math.abs(endX - clientX);
        if (startDelta < hoverRadius && endDelta >= hoverRadius) {
            return "start";
        } else if (startDelta >= hoverRadius && endDelta < hoverRadius) {
            return "end";
        } else if (startDelta < hoverRadius && endDelta < hoverRadius) {
            return startDelta < endDelta ? "start" : "end";
        } else if (clientX >= startX && clientX <= endX) {
            return startDelta;
        }
    }

    private onMouseMove = ({
        clientX,
        clientY,
        buttons,
    }: MouseEvent<HTMLCanvasElement>) => {
        const clicked = buttons === 1;
        this.onDragMove(clientX, clientY);
        if ((this.draggingTimeMarker || this.draggingRange) && !clicked) {
            this.onDragStop();
        }
    };

    private onMouseDown = ({
        clientX,
        clientY,
    }: MouseEvent<HTMLCanvasElement>) => {
        this.onDragStart(clientX, clientY);
    };

    private onTouchStart = (event: TouchEvent) => {
        const { clientX, clientY } = event.touches[0];
        this.onDragStart(clientX, clientY);
    };

    private onTouchMove = (event: TouchEvent) => {
        const { clientX, clientY } = event.touches[0];
        this.onDragMove(clientX, clientY);
    };

    private onTouchEnd = () => {
        this.onDragStop();
    };

    private onDragStart(clientX: number, clientY: number) {
        const time = this.mouseToTime(clientX);
        const range = this.mouseToRange(clientX, clientY);
        if (range) {
            this.draggingRange = range;
        } else {
            this.draggingTimeMarker = true;
            this.onSelectTime(time);
        }
    }

    @action
    private onDragStop() {
        this.draggingTimeMarker = false;
        this.draggingRange = undefined;
        this.hoverRange = undefined;
        const { hoverEvent, hoverAggregateEvent } = observeStore;
        const { onSelectEvent } = this.props;
        if (hoverEvent || hoverAggregateEvent) {
            onSelectEvent({
                event: hoverEvent,
                aggregate: hoverAggregateEvent,
            });
        }
    }

    @action
    private onDragMove(clientX: number, clientY: number) {
        const time = this.mouseToTime(clientX);
        this.hoverRange =
            (!this.draggingTimeMarker && this.mouseToRange(clientX, clientY)) ||
            undefined;
        if (!this.hoverRange) {
            const { event: hoveringEvent, aggregate: hoveringAggregate } =
                this.mouseToEvent(clientX, clientY) || {};
            if (hoveringEvent) {
                observeStore.hoverEvent = hoveringEvent;
                observeStore.hoverAggregateEvent = undefined;
            } else if (hoveringAggregate) {
                observeStore.hoverEvent = undefined;
                observeStore.hoverAggregateEvent = hoveringAggregate;
            } else {
                observeStore.hoverEvent = undefined;
                observeStore.hoverAggregateEvent = undefined;
            }
        }
        if (this.draggingTimeMarker) {
            if (observeStore.hoverEvent) {
                this.onSelectTime(parseIsoDate(observeStore.hoverEvent.time));
            } else {
                this.onSelectTime(time);
            }
        } else if (this.draggingRange) {
            const { selectedEvent, onChangeSelectedEvent } = this.props;
            function onChangeTimeRange(d1: Date, d2: Date) {
                defined(onChangeSelectedEvent)({
                    ...defined(selectedEvent),
                    time: d1.toISOString(),
                    endTime: d2.toISOString(),
                });
            }
            const { time: selectedEventTime, endTime: selectedEventEndTime } =
                defined(selectedEvent);
            if (!selectedEventEndTime) {
                return;
            }
            const start = parseIsoDate(selectedEventTime);
            const end = parseIsoDate(selectedEventEndTime);
            if (this.draggingRange === "start") {
                if (time.getTime() <= end.getTime()) {
                    onChangeTimeRange(time, end);
                } else {
                    this.draggingRange = "end";
                    onChangeTimeRange(end, time);
                }
            } else if (this.draggingRange === "end") {
                if (start.getTime() <= time.getTime()) {
                    onChangeTimeRange(start, time);
                } else {
                    this.draggingRange = "start";
                    onChangeTimeRange(time, start);
                }
            } else {
                const offset = this.draggingRange;
                const offsetTime = this.mouseToTime(clientX - offset);
                const offsetEnd = new Date(
                    offsetTime.getTime() + (end.getTime() - start.getTime()),
                );
                onChangeTimeRange(offsetTime, offsetEnd);
            }
        }
    }

    @action
    private onMouseOut = () => {
        observeStore.hoverEvent = undefined;
        observeStore.hoverAggregateEvent = undefined;
        if (!this.draggingRange) {
            this.hoverRange = undefined;
        }
    };

    private onWheel = (event: WheelEvent) => {
        event.preventDefault();
        const position = this.mouseToTime(event.clientX);
        appStore.timelineScale = Math.min(
            Math.max(
                minScale,
                appStore.timelineScale * Math.exp(event.deltaY * 0.002),
            ),
            maxScale,
        );
        const movement = (event.deltaX * this.span) / this.width;
        const offset =
            this.mouseToTime(event.clientX).getTime() - position.getTime();
        this.center = new Date(this.center.getTime() + movement - offset);
        this.moving = true;
        this.stopMoving();
        this.labels.clear();
    };

    private onResize = (width: number, height: number) => {
        this.width = width;
        this.height = height;
        const { canvas, ctx } = this;
        if (!canvas || !ctx) {
            return;
        }
        const pixelRatio = window.devicePixelRatio;
        canvas.width = width * pixelRatio;
        canvas.height = height * pixelRatio;
        ctx.scale(pixelRatio, pixelRatio);
        this.labels.clear();
    };

    private getLabel(
        label: string,
        style: string | CanvasGradient | CanvasPattern = this.colors
            .foregroundColor,
    ): HTMLCanvasElement | undefined {
        const cached = this.labels.get(label);
        if (cached) {
            return cached;
        }
        const fontSize = 10;
        const font = `${fontSize}px Moderat, sans-serif`;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            captureError(new Error("No context"));
            return;
        }
        ctx.font = font;
        const { width } = ctx.measureText(label);
        const height = fontSize * 1.2;
        const pixelRatio = window.devicePixelRatio;
        canvas.width = width * pixelRatio;
        canvas.height = height * pixelRatio;
        ctx.scale(pixelRatio, pixelRatio);
        ctx.textBaseline = "alphabetic";
        ctx.fillStyle = style;
        ctx.font = font;
        ctx.fillText(label, 0, fontSize);

        this.labels.set(label, canvas);
        return canvas;
    }
}

// Draw a rectangle with rounded corners.
function rect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number = 0,
) {
    if (width <= 0 || height <= 0) {
        return;
    }
    if (radius) {
        const r = Math.min(radius, width / 2, height / 2);
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + width - r, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + r);
        ctx.lineTo(x + width, y + height - r);
        ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
        ctx.lineTo(x + r, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
    } else {
        ctx.rect(x, y, width, height);
    }
}

// Top half of a rectagle. Not that fancy.
function fancyBracket(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
) {
    if (width <= 0 || height <= 0) {
        return;
    }
    ctx.moveTo(x, y + height / 2);
    ctx.lineTo(x, y);
    ctx.lineTo(x + width, y);
    ctx.lineTo(x + width, y + height / 2);
}

// Same as rect, except only the bottom corners are rounded.
function tab(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
) {
    if (width <= 0 || height <= 0) {
        return;
    }
    const r = Math.min(radius, width / 2, height / 2);
    ctx.moveTo(x, y);
    ctx.lineTo(x + width, y);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y);
}
